import React from "react";
import { useSelector } from "react-redux";

import { Collapsible } from "~/components/collapsible";
import { CollapsibleContent } from "~/components/collapsible/content";
import { CollapsibleTrigger } from "~/components/collapsible/trigger";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";

import { selectLocationPathname } from "../../app/state/route/RouteSelectors";
import { Link } from "../../components/react/link";

import { MainMenuAccordionButton } from "./MainMenuAccordionButton";

interface MainMenuItemProps {
	active?: boolean;
	disabled?: boolean;
	className?: string;
	href?: string;
	to?: string;
	level?: number;
	icon?: React.ReactNode;
	label?: React.ReactNode | string;
	extra?: React.ReactNode;
}

const MainMenuItem: React.FC<React.PropsWithChildren<MainMenuItemProps>> = ({
	active,
	disabled,
	className,
	icon,
	label,
	extra,
	level = 0,
	children,
	...props
}) => (
	<ItemLink
		data-disabled={disabled}
		data-testid="sts-view-menu-item-layout"
		disabled={disabled}
		{...props}
		className={cx(
			"relative flex cursor-pointer select-none items-center justify-between gap-4 px-6 py-1.5 transition-colors",
			level > 0 ? "font-normal text-grey-600 text-sm/4.5" : "font-medium text-15/6 text-grey-800",
			disabled && "cursor-not-allowed bg-transparent opacity-30",
			className,
			{ active, disabled },
		)}
	>
		{icon && <div className="flex w-[18px] shrink-0 grow-0 items-center justify-center">{icon}</div>}

		<Truncated className="shrink grow transition-colors hover:text-primary-600 peer-hover:text-primary-600">
			{label}
		</Truncated>

		<div className="z-1 leading-none" data-testid="sts-view-menu-item-layout-extra">
			{extra}
			{children}
		</div>
	</ItemLink>
);

const ItemLink: React.FC<
	React.PropsWithChildren<{
		className?: string;
		disabled?: boolean;
		href?: string;
		to?: string;
	}>
> = ({ className, disabled, href, to, ...props }) => {
	const currentPath = useSelector(selectLocationPathname);

	if (to != null && to !== currentPath && !disabled) {
		const { children, ...restProps } = props;

		return (
			<div {...restProps} className={className}>
				<Link className="peer absolute inset-0" to={to} />
				{children}
			</div>
		);
	}

	if (href != null && !disabled) {
		return <a href={href} rel="noopener noreferrer" target="_blank" {...props} className={cx("peer", className)} />;
	}

	return <div {...props} className={cx("peer", className)} />;
};

interface MainMenuItemLayoutProps extends MainMenuItemProps {
	defaultOpen?: boolean;
	children?: React.ReactNode;
}

export const MainMenuItemLayout: React.FC<MainMenuItemLayoutProps> = ({ children, defaultOpen, ...props }) => {
	const [open, setOpen] = React.useState(defaultOpen);

	if (!children) {
		return <MainMenuItem {...props} />;
	}

	const openWithFallback = open ?? defaultOpen ?? true;

	return (
		<Collapsible onOpenChange={setOpen} open={openWithFallback}>
			<CollapsibleTrigger asChild>
				<MainMenuItem {...props}>
					<MainMenuAccordionButton open={openWithFallback} />
				</MainMenuItem>
			</CollapsibleTrigger>

			<CollapsibleContent>{children}</CollapsibleContent>
		</Collapsible>
	);
};
